'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Input } from '@app/app/_components/Input/Input';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { EuropeanUnionIcon } from '@app/app/_icons/EuropeanUnionIcon';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import {
    requestTransportInsurance,
    TransportInsuranceRequestResult,
} from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useFormState } from 'react-dom';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { RequestTransportInsurance } from '@app/service/eshop/transport/schemas/RequestTransportInsurance';
import { FontSize } from '@app/domain/model/FontSize';
import { useHighContrastMode } from '@app/app/_context/AccessibilityContext/useHighContrastMode';
import { useFontSize } from '@app/app/_context/AccessibilityContext/useFontSize';
import { trackStartCalculation } from '@app/infrastructure/gtm/transport/trackStartCalculation';
import { ensureString } from '@app/lib/util/ensureString';
import { validateCompanyCode } from '@app/lib/company-code/validateCompanyCode';

interface Props {
    onSuccess?: (result: TransportInsuranceRequestResult) => void;
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
}

export function TransportLandingSection({
    onSuccess,
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
}: Props) {
    const t = useTranslations();
    const isMobile = useIsMobile(1024);
    const locale = useLocale();
    const parameters = useAppParameters();
    const [isLoading, setIsLoading] = useState(false);
    const isHighContrastMode = useHighContrastMode();
    const fontSize = useFontSize();
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);

    const [formState, formAction] = useFormState(requestTransportInsurance, { status: FormStatus.Pending });

    useEffect(() => {
        if (formState.status === FormStatus.Success && formState.data != null) {
            setIsLoading(false);
            setClientValidationErrors(null);
            onSuccess?.(formState.data);
        } else if (formState.status === FormStatus.Error) {
            setIsLoading(false);
        }
    }, [formState, onSuccess]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setClientValidationErrors(null);

        const formData = new FormData(e.currentTarget);
        const licencePlate = ensureString(formData.get('licencePlate'), '');
        const personalCode = ensureString(formData.get('personalCode'), '');

        try {
            await RequestTransportInsurance.validate(
                {
                    licencePlate: licencePlate,
                    personalCode: personalCode,
                    acceptPrivacyPolicy: formData.get('acceptPrivacyPolicy') === 'true',
                },
                { abortEarly: false },
            );

            const isCompany = validateCompanyCode(personalCode).isValid;
            trackStartCalculation({
                licencePlate: licencePlate ?? '',
                isCompany,
            });

            setIsLoading(true);
            formAction(formData);
        } catch (error) {
            if (error instanceof ValidationError) {
                const violations = buildViolations(error, 'general.validationError');
                setClientValidationErrors(violations.violations ?? null);
            }
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
            <GlobalLoader isVisible={isLoading} text={t('eshop.transport.loadingVehicleInformation')} />
            <div className="relative flex flex-col bg-white py-12 lg:mb-[500px] lg:px-16">
                {!isMobile && !isHighContrastMode && fontSize !== FontSize.ExtraLarge && fontSize !== FontSize.Huge && (
                    <div className="absolute right-0 top-0 h-full w-[70%] xl:w-full">
                        <Image
                            src={backgroundImageSrc}
                            fill
                            draggable={false}
                            className="object-cover object-center"
                            alt={t('eshop.transport.backgroundImageAlt')}
                            unoptimized
                        />
                    </div>
                )}
                <form onSubmit={handleSubmit} className="relative z-10 px-4 md:max-w-md">
                    <Heading className="mb-8 break-words text-4xl 2xl:text-nowrap">
                        {t('eshop.transport.landingTitle')}
                    </Heading>

                    <div className="mb-10 flex flex-col gap-6">
                        {formState.status === FormStatus.Error && formState.violations == null && (
                            <Message>{t(formState.message)}</Message>
                        )}

                        <div className="flex w-full flex-col gap-6 sm:flex-row">
                            <Input
                                className="flex-grow sm:max-w-32"
                                inputClassName="uppercase"
                                name="licencePlate"
                                label={t('eshop.transport.licencePlateLabel')}
                                placeholder={t('eshop.transport.licencePlatePlaceholder')}
                                noSpaces
                                noSymbols
                                size={6}
                                prefixClassName="bg-blue-600 px-2 rounded-l-md mr-3"
                                prefix={
                                    <div className="flex flex-col items-center justify-center">
                                        <EuropeanUnionIcon className="size-5 text-yellow-500" />
                                        <div className="text-[0.625rem] font-bold text-white">
                                            {t('general.countries.lt')}
                                        </div>
                                    </div>
                                }
                                description={
                                    <InformationPopover
                                        className="underline"
                                        content={t('eshop.transport.licencePlateNotRegisteredPopover')}>
                                        {t('eshop.transport.licencePlateNotRegisteredLink')}
                                    </InformationPopover>
                                }
                                error={clientValidationErrors?.licencePlate ?? formState.violations?.licencePlate}
                            />

                            <Input
                                className="flex-grow"
                                name="personalCode"
                                noSpaces
                                noSymbols
                                label={t('eshop.transport.personalCompanyCodeLabel')}
                                labelAddon={
                                    <InformationPopover content={t('eshop.transport.personalCompanyCodeTooltip')}>
                                        <QuestionCircleIcon className="size-4 text-neutral-500" />
                                    </InformationPopover>
                                }
                                placeholder={t('eshop.transport.personalCompanyCodePlaceholder')}
                                description={
                                    <InformationPopover
                                        content={t('eshop.transport.personalCompanyCodeDescriptionPopover')}
                                        className="underline">
                                        {t('eshop.transport.personalCompanyCodeDescriptionLink')}
                                    </InformationPopover>
                                }
                                error={clientValidationErrors?.personalCode ?? formState.violations?.personalCode}
                            />
                        </div>

                        <CheckboxInput
                            name="acceptPrivacyPolicy"
                            label={t.rich('eshop.transport.acceptPrivacyPolicyLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.privacyPolicy
                                                : parameters.eshopInfoUrls.privacyPolicyEn
                                        }>
                                        {chunks}
                                    </Link>
                                ),
                            })}
                            error={
                                clientValidationErrors?.acceptPrivacyPolicy ?? formState.violations?.acceptPrivacyPolicy
                            }
                        />
                    </div>

                    <div className="grid gap-6 max-sm:grid-cols-1 sm:grid-cols-2">
                        <Button rightAddon={<ArrowRightIcon className="size-4" />}>{t('general.next')}</Button>
                        <Button
                            href={parameters.eshopInfoUrls.mtplCasco}
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}
                            variant="outlined">
                            {t('eshop.general.readMore')}
                        </Button>
                    </div>
                </form>
            </div>
            <div className="relative h-[600px] w-full lg:hidden">
                <Image
                    src={mobileImageSrc}
                    fill
                    className="object-cover"
                    alt={t('eshop.transport.backgroundImageAlt')}
                    unoptimized
                />
            </div>

            <EsalesTransportFooter stakeholderLogoSrc={stakeholderLogoSrc} vigLogoSrc={vigLogoSrc} />
        </div>
    );
}
